import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  Database,
  Users,
  History,
  LogOut,
  Boxes,
  BookOpen,
  Zap,
} from "lucide-react";
import { cn } from "../lib/utils";

const items = [
  { to: "/admin", label: "Visão Geral", shortLabel: "Início", icon: LayoutDashboard, end: true, id: "nav-overview" },
  { to: "/admin/contas-api", label: "Contas de API", shortLabel: "Contas", icon: Database, id: "nav-accounts" },
  { to: "/admin/usuarios", label: "Usuários do App", shortLabel: "Usuários", icon: Users, id: "nav-users" },
  { to: "/admin/logs", label: "Logs de Requisições", shortLabel: "Logs", icon: History, id: "nav-logs" },
  { to: "/admin/ia-massa", label: "Teste em Massa", shortLabel: "Massa", icon: Zap, id: "nav-bulk-ia" },
  { to: "/admin/documentacao", label: "Documentação API", shortLabel: "Docs", icon: BookOpen, id: "nav-docs" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Sidebar desktop (fixa) */}
      <aside className="hidden lg:flex w-64 shrink-0 bg-white border-r border-slate-200 flex-col fixed inset-y-0 left-0 z-30">
        <div className="px-6 py-6 border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-slate-900 flex items-center justify-center">
              <Boxes className="h-4.5 w-4.5 text-white" strokeWidth={2.25} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">Painel Admin</p>
              <p className="text-sm font-semibold text-slate-900 -mt-0.5">Gerenciador de APIs</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5" data-testid="sidebar-nav">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={it.id}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-200",
                    isActive
                      ? "bg-slate-100 text-slate-900 font-medium"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  )
                }
              >
                <Icon className="h-4 w-4" strokeWidth={2} />
                <span>{it.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <div className="px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-medium">Conectado como</p>
            <p className="text-sm font-medium text-slate-900 truncate" data-testid="sidebar-user-email">
              {user?.email}
            </p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="mt-1 w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors duration-200"
          >
            <LogOut className="h-4 w-4" />
            <span>Sair</span>
          </button>
        </div>
      </aside>

      {/* Header mobile */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-slate-200 h-14 flex items-center justify-between px-4">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-md bg-slate-900 flex items-center justify-center">
            <Boxes className="h-4 w-4 text-white" strokeWidth={2.25} />
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-[0.22em] text-slate-500 font-medium leading-tight">Painel Admin</p>
            <p className="text-xs font-semibold text-slate-900 leading-tight">Gerenciador de APIs</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-button-mobile"
          className="p-2 text-slate-600 hover:text-slate-900 rounded-md hover:bg-slate-100"
          aria-label="Sair"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </header>

      {/* Main */}
      <main className="lg:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0 min-h-screen">
        <div className="px-4 sm:px-6 lg:px-8 py-5 lg:py-8">
          <Outlet />
        </div>
      </main>

      {/* Bottom nav mobile */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 grid grid-cols-6 h-16 safe-bottom"
        data-testid="mobile-bottom-nav"
      >
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`${it.id}-mobile`}
              className={({ isActive }) =>
                cn(
                  "flex flex-col items-center justify-center gap-1 text-[10px] transition-colors duration-200",
                  isActive
                    ? "text-slate-900 font-semibold"
                    : "text-slate-500 hover:text-slate-900"
                )
              }
            >
              {({ isActive }) => (
                <>
                  <div className={cn("flex items-center justify-center h-7 w-7 rounded-md transition-colors",
                    isActive ? "bg-slate-100" : "")}>
                    <Icon className="h-4 w-4" strokeWidth={2} />
                  </div>
                  <span className="leading-none">{it.shortLabel}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
